/**
 * Which section model a product profile runs on.
 *
 * SHARED WITH THE CUSTOMIZER. Both repos must answer this question identically: the admin
 * portal decides which editor to show and which overlay to draw, the Customizer decides
 * which renderer and which interaction model to use, and a product that answers
 * differently in the two places is a product whose sections move between authoring and
 * engraving.
 */

export type SectionModel = 'cylindrical' | 'frame'

/** The shape of the bits of `view_profile.data` this resolver reads. */
export interface ViewProfileDataLike {
  section_model?: string | null
  /**
   * Still read, but ONLY as a legacy fallback — see resolveSectionModel. This field's real
   * job is generating geometry UVs, which is a different question from where sections sit.
   */
  uv_projection?: string | null
}

/**
 * Resolve the section model for a profile.
 *
 * The order matters, and each step exists for a reason:
 *
 * 1. An explicit `section_model` always wins. It is the only field whose job this is.
 *
 * 2. Otherwise, a section that already carries a `placement` proves the profile was
 *    authored as frames, whatever the view profile says. This is belt-and-braces for rows
 *    written before the field existed, and it means placement data can never be silently
 *    ignored and rendered through cylindrical maths.
 *
 * 3. Otherwise cylindrical — which is what every consumer has always assumed when the
 *    projection field was absent, and what 98 of the 99 live profiles want.
 *
 * Note what is NOT here: `uv_projection === 'planar'` does not imply frames. That field
 * also drives geometry UV generation (applyUvProjection rewrites the UV attribute with
 * it), so a round product can legitimately need cylindrical UVs for its base texture AND
 * frames for its sections. Deriving one from the other makes those two settings
 * impossible to move independently, which is exactly what the equivalence harness needs
 * to do.
 */
export function resolveSectionModel(
  data: ViewProfileDataLike | null | undefined,
  sections?: ReadonlyArray<{ target_object?: { placement?: unknown } | null } | null> | null
): SectionModel {
  const explicit = data?.section_model
  if (explicit === 'frame' || explicit === 'cylindrical') return explicit

  if (sections?.some((s) => s?.target_object?.placement)) return 'frame'

  return 'cylindrical'
}

/** True when this profile should run the frame projector rather than the legacy path. */
export function usesFrames(
  data: ViewProfileDataLike | null | undefined,
  sections?: ReadonlyArray<{ target_object?: { placement?: unknown } | null } | null> | null
): boolean {
  return resolveSectionModel(data, sections) === 'frame'
}
